import { useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import { 
  Bell, BellOff, Check, Trash2, Package, CreditCard, 
  Gift, MessageCircle, Crown, X, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const NotificationIcon = ({ type }) => {
  const icons = {
    purchase: Package,
    payment: CreditCard,
    gift: Gift,
    message: MessageCircle,
    reward: Crown,
    default: Bell
  };
  const Icon = icons[type] || icons.default;
  return <Icon className="w-4 h-4" />;
};

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Navigate if there's a link
    if (notification.link) {
      window.location.href = notification.link;
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true,
        locale: nl 
      });
    } catch {
      return 'Recent';
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="relative text-gray-300 hover:text-white"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-[#0A0A0A] border-white/10 p-0 max-h-[400px] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-medium">Notificaties</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={markAllAsRead}
              className="text-xs text-[#D4AF37] hover:text-[#D4AF37]/80"
            >
              <Check className="w-3 h-3 mr-1" />
              Alles gelezen
            </Button>
          )}
        </div>

        {/* Notification List */}
        <div className="max-h-[300px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <BellOff className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Geen notificaties</p>
            </div>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <div
                key={notification.id}
                className={`flex items-start gap-3 p-4 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/5 ${
                  !notification.read ? 'bg-[#D4AF37]/5' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  notification.read ? 'bg-white/10' : 'bg-[#D4AF37]/20'
                }`}>
                  <NotificationIcon type={notification.type} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.read ? 'font-medium' : 'text-gray-300'}`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatTime(notification.created_at)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearNotification(notification.id);
                  }}
                  className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-white/10">
            <Button 
              variant="ghost" 
              className="w-full text-sm text-gray-400 hover:text-white"
              onClick={() => setIsOpen(false)}
            >
              Bekijk alle notificaties
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
